import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import LockIcon from "@mui/icons-material/Lock";
import { useAllTools, useUserTools, useSetUserTools } from "../../hooks/useToolGrants";
import toast from "react-hot-toast";

interface UserToolsDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName?: string;
  userRole?: string;
}

export const UserToolsDialog: React.FC<UserToolsDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
  userRole,
}) => {
  const { data: allTools, isLoading: toolsLoading } = useAllTools();
  const { data: userTools, isLoading: grantsLoading } = useUserTools(userId);
  const setToolsMutation = useSetUserTools(userId);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (isAdmin && allTools) {
      setSelected(new Set(allTools.map((t) => t.id)));
    } else if (userTools) {
      setSelected(new Set(userTools));
    }
  }, [userTools, allTools, isAdmin]);

  const toggle = (toolId: string) => {
    if (isAdmin) return;
    const next = new Set(selected);
    if (next.has(toolId)) next.delete(toolId);
    else next.add(toolId);
    setSelected(next);
  };

  const handleSave = async () => {
    try {
      await setToolsMutation.mutateAsync(Array.from(selected));
      toast.success("Accessi aggiornati");
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const loading = toolsLoading || grantsLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "1rem" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <BuildIcon sx={{ color: "#1565C0" }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Accessi tool
          </Typography>
          {userName && (
            <Typography variant="caption" color="text.secondary">{userName}</Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pt: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box>
            {isAdmin && (
              <Alert
                icon={<LockIcon fontSize="small" />}
                severity="info"
                sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.8rem" }}
              >
                Gli admin hanno sempre accesso a tutti i tool, indipendentemente da questi grant.
              </Alert>
            )}
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.72rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3, py: 0.75, borderBottom: "1.5px solid", borderColor: "divider" } }}>
                  <TableCell>Tool</TableCell>
                  <TableCell align="center" sx={{ width: 56 }}>Accesso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allTools?.map((tool) => (
                  <TableRow
                    key={tool.id}
                    hover={!isAdmin}
                    onClick={() => toggle(tool.id)}
                    sx={{ cursor: isAdmin ? "default" : "pointer", "& td": { py: 0.5, borderBottom: "1px solid", borderColor: "rgba(0,0,0,0.06)" } }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{tool.name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={selected.has(tool.id)}
                        disabled={isAdmin}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggle(tool.id); }}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={setToolsMutation.isLoading}>Annulla</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || setToolsMutation.isLoading || isAdmin}
        >
          {setToolsMutation.isLoading ? <CircularProgress size={22} /> : "Salva"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
